import React from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Grid, Stack, Box, Container, Typography, useMediaQuery, useTheme, Skeleton } from "@mui/material";
import TypeOfAccommodation from "./TypeOfAccommodation";
import HeroSection from "../../components/common/HeroSection";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getAccommodationPage } from "../../services/FindAccommodationPageService";

const Accommodation = () => {
  const { country_id, country_name } = useSelector((state) => state.auth || {});
  
  const { data: programData, isLoading: loading } = useQuery({
    queryKey: ["accommodationPage", country_id],
    queryFn: async () => {
      if (!country_id) return null;
      const res = await getAccommodationPage(country_id);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      }
      return null;
    },
    enabled: !!country_id,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getResponsiveHtml = (html) => {
    if (!html) return "";
    if (!isMobile) return html.replace(/<br\s*\/?>/gi, "").replace(/&nbsp;/gi, " ").replace(/\u00A0/gi, " ").replace(/\[empty_line\]/gi, "<br />");
    return html
      .replace(/font-size:\s*\d+px/gi, "font-size: 18px")
      .replace(/font-weight:\s*900/gi, "font-weight: 700")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/gi, " ")
      .replace(/\[empty_line\]/gi, "<br />");
  };

  const parseDocuments = (html) => {
    if (!html) return [];
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const items = Array.from(temp.querySelectorAll("li"));
    return items.map((li) => {
      const link = li.querySelector("a");
      const fullText = li.innerText;
      const name = link?.innerText?.trim() || "";
      const url = link?.href || "#";
      const parts = fullText.split("-");
      const location = parts[1]?.trim() || "";
      return { name, url, location };
    });
  };

  if (loading) {
    return (
      <Container
        sx={{
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="text" height={60} width="80%" />
            <Skeleton variant="text" height={30} width="90%" />
            <Skeleton variant="text" height={30} width="85%" />
            <Stack direction="row" spacing={2} mt={3}>
              <Skeleton variant="rounded" width={140} height={45} />
              <Skeleton variant="rounded" width={140} height={45} />
            </Stack>
            <Stack spacing={2} mt={3}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="75%" />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!programData)
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <p>No accommodation data found for {country_name || "selected country"}.</p>
      </Box>
    );

  const heroTitle = programData.hero_section_title || "";
  const heroImageType = programData.hero_section_image_type || "image";
  const heroSubtitle = programData.hero_section_desc || "";
  const heroImage = programData.hero_section_image || "";
  const about_desc = programData.about_desc || "";
  const note_acc = programData.details_desc || ""
  const documents = programData.documents || "";

  const documentList = parseDocuments(documents);

  return (
    <>
      <HeroSection
        titleContent={heroTitle}
        subtitleContent={heroSubtitle}
        heroImage={heroImage}
        heroImageType={heroImageType}
      />
      <TypeOfAccommodation />
      {about_desc && (
        <Container sx={{
          py: { xs: 2, sm: 2, md: 3 },
          px: { xs: 3, sm: 4, md: 6 },
          paddingLeft: { xs: "24px !important", md: "48px !important" },
          paddingRight: { xs: "24px !important", md: "48px !important" },
        }}>
          <Box sx={{ display: "inline-block", position: "relative", mb: 2, "&::after": { content: '""', position: "absolute", left: 0, bottom: -8, width: "200px", height: "2px", backgroundColor: "#fd0000", opacity: "0.5" } }}>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: "22px", sm: "26px", md: "30px" }, color: "#000" }}>
              Book Your Accommodation
            </Typography>
          </Box>
          <Box sx={{ borderRadius: "10px", m: 'auto', mt: { xs: 2, md: 0 }, width: "100%", zIndex: 2, display: "flex" }}>
            <Typography sx={{ fontSize: { xs: "14px", sm: "16px" }, textAlign: "justify" }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(about_desc) }} />
          </Box>

          {documents && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr 1fr" }, gap: 2, mt: 3 }}>
              {documentList.map((item, i) => (
                <Box key={i} sx={{ p: 2, borderRadius: "12px", background: "white", border: "2px solid #002045", transition: "0.3s", cursor: "pointer", "&:hover": { boxShadow: "0px 8px 20px rgba(0,0,0,0.4)", transform: "translateY(-4px)" } }} onClick={() => window.open(item.url, "_blank")}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: "8px", background: "#fff", border: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, overflow: "hidden" }}>
                      <img src={`https://www.google.com/s2/favicons?sz=64&domain_url=${item.url}`} alt={item.name} style={{ width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "16px", mb: 1, textTransform: 'capitalize' }}>{item.name}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "13px", color: "black", mb: 2 }}>Accommodation platform for students and expats.</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {item.location.split(",").slice(0, 4).map((loc, idx) => (
                      <Box key={idx} sx={{ fontSize: "11px", px: 1, py: 0.5, borderRadius: "20px", background: "#0020450e", color: "#002045", fontWeight: 600, textTransform: "capitalize" }}>{loc.trim()}</Box>
                    ))}
                    {item.location.split(",").length > 4 && <Box sx={{ fontSize: "11px", px: 1, py: 0.5, borderRadius: "20px", background: "#0020450e", color: "#002045", fontWeight: 600 }}>More+</Box>}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {note_acc && (
            <Box sx={{ borderRadius: "10px", mt: { xs: 2, md: 2 }, width: "100%", zIndex: 2, display: "flex", alignItems: "flex-start", gap: 1.2, p: 1.5, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "black", mt: "2px" }} />
              <Typography sx={{ fontSize: "13px", color: "black", lineHeight: 1.6, textAlign: "justify" }} dangerouslySetInnerHTML={{ __html: getResponsiveHtml(note_acc) }} />
            </Box>
          )}
        </Container>
      )}
    </>
  );
};

export default Accommodation;
